// src/screens/Calcleite/CalcleiteMain.js

import React, { useState, useMemo, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { signOut } from 'firebase/auth';
import { auth } from '../../firebaseconfig';
import { useAuth } from '../../hooks/useAuth';
import './CalcleiteMain.css';

// Importe os ícones
import logo from "../../assets/calcleite.png";
import manualIcon from "../../assets/manual.png";
import producaoIcon from "../../assets/producao.png";
import capitalIcon from "../../assets/capital.png";
import custosIcon from "../../assets/custos.png";
import receitaIcon from "../../assets/receita.png";
import relatorioIcon from "../../assets/relatorio.png";

const toDate = (timestamp) => {
  if (!timestamp) return null;
  if (typeof timestamp.toDate === "function") {
    return timestamp.toDate();
  }
  const date = new Date(timestamp);
  return isNaN(date) ? null : date;
};

const CalcleiteMain = () => {
  const { user, userData, loading } = useAuth();
  const navigate = useNavigate();

  const [modalVisible, setModalVisible] = useState(false);
  const [cancelLoading, setCancelLoading] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [isSyncing, setIsSyncing] = useState(false);

  const APP_TAG = 'calcleite';
  const lastSyncAttemptRef = useRef(0);

  // Função para forçar a sincronização dos dados da assinatura com o backend
  const syncSubscription = async () => {
    if (!user || isSyncing) return;

    const now = Date.now();
    if (now - lastSyncAttemptRef.current < 10000) { // Cooldown de 10s
      return;
    }
    
    setIsSyncing(true);
    lastSyncAttemptRef.current = now;
    
    try {
      const token = await user.getIdToken(true);
      const response = await fetch("https://backendcustosagro-e51a.onrender.com/sync-subscription", {
        method: "POST",
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ uid: user.uid }),
      });
      if (!response.ok) throw new Error("Falha ao sincronizar dados.");
      console.log("Sincronização de assinatura concluída.");
    } catch (error) {
      console.error("Erro ao sincronizar assinatura:", error);
    } finally {
      setIsSyncing(false);
    }
  };

  // Sincroniza os dados ao carregar a página
  useEffect(() => {
    if (user) {
        syncSubscription();
    }
  }, [user]); // Roda sempre que o objeto 'user' é carregado/alterado

  // Verifica se o usuário tem acesso a este app
  useEffect(() => {
    if (!loading && userData && !userData[APP_TAG]) {
      alert("Sua assinatura para o CalcLeite não está ativa ou expirou.");
      navigate("/calcleite/planos");
    }
  }, [userData, loading, navigate]);

  const subscriptionData = useMemo(() => 
    userData?.subscriptions?.[APP_TAG] || {}, 
  [userData]);

  const dataTermino = useMemo(() => {
    const periodEnd = toDate(subscriptionData.currentPeriodEnd);
    return periodEnd;
  }, [subscriptionData]);
  
  const diasRestantes = useMemo(() => {
    if (!dataTermino) return "N/A";
    const hoje = new Date();
    const diffTime = Math.max(0, dataTermino.getTime() - hoje.getTime());
    return Math.ceil(diffTime / (1000 * 60 * 60 * 24)).toString();
  }, [dataTermino]);

  const dataFormatada = useMemo(() => 
    dataTermino ? dataTermino.toLocaleDateString("pt-BR") : "N/A",
  [dataTermino]);
  
  const handleLogout = async () => {
    await signOut(auth);
    navigate("/login");
  };

  const handleCancelSubscription = async () => {
    if (!window.confirm("Você tem certeza que deseja cancelar sua assinatura? Ela permanecerá ativa até o fim do período de cobrança.")) {
        return;
    }
    setCancelLoading(true);
    try {
      const token = await user.getIdToken(true);
      const response = await fetch("https://backendcustosagro-e51a.onrender.com/cancel-subscription", {
        method: "POST",
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ uid: user.uid, app: APP_TAG }),
      });
      const result = await response.json();
      if (!result.success) throw new Error(result.error || "Falha ao cancelar assinatura");
      
      // Força a sincronização dos dados para atualizar a UI em tempo real
      await syncSubscription();

      alert("Sucesso! Sua assinatura foi marcada para cancelamento no final do período.");
      setModalVisible(false);
    } catch (error) {
      alert(`Erro: ${error.message}`);
    } finally {
      setCancelLoading(false);
    }
  };

  const handleDeleteAccount = async () => {
    if (!window.confirm("ATENÇÃO! Você tem certeza que deseja excluir sua conta permanentemente? Esta ação é irreversível e cancelará todas as suas assinaturas ativas imediatamente.")) {
        return;
    }
    setDeleteLoading(true);
    try {
        const token = await user.getIdToken(true);
        const response = await fetch("https://backendcustosagro-e51a.onrender.com/delete-account", {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        });
        const result = await response.json();
        if(!result.success) throw new Error(result.error || "Não foi possível excluir a conta.");
        alert("Sua conta foi excluída com sucesso.");
        navigate("/login");
    } catch (error) {
        alert(`Erro: ${error.message}`);
    } finally {
        setDeleteLoading(false);
    }
  };

  if (loading) {
    return <div className="clm-loading-container">Carregando...</div>;
  }
  
  // Constrói o nome do campo de cancelamento dinamicamente para verificação
  const willCancelField = `willCancelAtPeriodEnd${APP_TAG.charAt(0).toUpperCase() + APP_TAG.slice(1)}`;
  const willCancel = userData?.[willCancelField] || false;

  return (
    <div className="clm-container">
      <button className="clm-settings-button" onClick={() => setModalVisible(true)}>
        Configurações {isSyncing && "🔄"}
      </button>

      <header className="clm-header-content">
        <img src={logo} alt="CalcLeite Logo" className="clm-logo" />
        <h1 className="clm-title">CalcLeite</h1>
        <p className="clm-subtitle">
          Calcule e simule custos e rentabilidade da produção de leite
        </p>
      </header>

      <div className="clm-grid">
        <button className="clm-card" onClick={() => navigate('/calcleite/app/instrucoes')}>
          <img src={manualIcon} alt="Ícone de manual" className="clm-icon" />
          <span>INSTRUÇÕES</span>
        </button>
        <button className="clm-card" onClick={() => navigate('/calcleite/app/producao')}>
          <img src={producaoIcon} alt="Ícone de produção" className="clm-icon" />
          <span>PRODUÇÃO E PRODUTIVIDADE</span>
        </button>
        <button className="clm-card" onClick={() => navigate('/calcleite/app/capital')}>
          <img src={capitalIcon} alt="Ícone de capital" className="clm-icon" />
          <span>CUSTO DO CAPITAL INVESTIDO E DEPRECIAÇÃO</span>
        </button>
        <button className="clm-card" onClick={() => navigate('/calcleite/app/custos')}>
          <img src={custosIcon} alt="Ícone de custos" className="clm-icon" />
          <span>CUSTO OPERACIONAL E TOTAL</span>
        </button>
        <button className="clm-card" onClick={() => navigate('/calcleite/app/receita')}>
          <img src={receitaIcon} alt="Ícone de receita" className="clm-icon" />
          <span>RECEITA E RENTABILIDADE</span>
        </button>
        <button className="clm-card" onClick={() => navigate('/calcleite/app/relatorio')}>
          <img src={relatorioIcon} alt="Ícone de relatório" className="clm-icon" />
          <span>RELATÓRIO FINAL</span>
        </button>
      </div>

      {modalVisible && (
        <div className="clm-modal-backdrop">
          <div className="clm-modal-content">
            <h2>Configurações</h2>
            
            <p><strong>Email:</strong> {userData?.customerEmail || 'N/A'}</p>
            <p><strong>Plano:</strong> {subscriptionData.plan || 'Nenhum'}</p>
            <p><strong>Status:</strong> {subscriptionData.status === 'trialing' ? 'Período de teste' : (subscriptionData.status || 'N/A')}</p>
            {diasRestantes !== "N/A" && <p><strong>Dias restantes:</strong> {diasRestantes}</p>}
            
            {dataFormatada !== "N/A" && (
                <p>
                    <strong>{willCancel ? "Sua assinatura expira em:" : "Próxima cobrança:"}</strong> {dataFormatada}
                </p>
            )}

            <div className="clm-modal-actions">
              {!willCancel && subscriptionData.status !== 'canceled' ? (
                <button className="clm-modal-button cancel" onClick={handleCancelSubscription} disabled={cancelLoading}>
                  {cancelLoading ? 'Cancelando...' : 'Cancelar Assinatura'}
                </button>
              ) : (
                <p className="clm-cancelled-text">Sua assinatura já foi cancelada e não será renovada.</p>
              )}
              <button className="clm-modal-button logout" onClick={handleLogout}>Sair</button>
            </div>

            <button className="clm-modal-button close" onClick={() => setModalVisible(false)}>Fechar</button>
          </div>
        </div>
      )}

      <footer className="clm-footer">
        <p>Copyright © 2024. Todos os Direitos Reservados.</p>
      </footer>
    </div>
  );
};

export default CalcleiteMain;